import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { useAuth } from "./contexts/AuthContext";
import { Settings } from "lucide-react";
import { hasAgencyFeature } from "@/utils/agencyFeatures";

/**
 * Protected route component that requires authentication
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @param {string[]} [props.allowedRoles] - Array of allowed user roles for this route
 * @param {boolean} [props.requireContentConfig] - If true, client must have content config to access
 * @param {boolean} [props.requireClientifyId] - If true, agency_admin/consultant must have clientify_id=true
 * @param {string} [props.requireAgencyFeature] - Agency feature flag required to access (see utils/agencyFeatures)
 * @returns {JSX.Element}
 */
export function ProtectedRoute({
  children,
  allowedRoles = [],
  requireContentConfig = false,
  requireClientifyId = false,
  requireAgencyFeature = null,
}) {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-muted-foreground">Verificando autenticación...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Tras el alta pública, la agencia y el nombre se piden antes de ver la app
  if (user.profile_pending) {
    return <Navigate to="/bienvenida" replace />;
  }

  // Check role-based access if allowedRoles is specified
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <AccessDenied />;
  }

  if (requireAgencyFeature && !hasAgencyFeature(user, requireAgencyFeature)) {
    return <AccessDenied />;
  }

  // Check content config requirement for client routes
  if (requireContentConfig && user.role === "client" && !user.has_content_config) {
    return <Navigate to="/dashboard" replace />;
  }

  if (
    requireClientifyId &&
    ["agency_admin", "consultant"].includes(user.role) &&
    !user.clientify_id
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  allowedRoles: PropTypes.arrayOf(PropTypes.string),
  requireContentConfig: PropTypes.bool,
  requireClientifyId: PropTypes.bool,
  requireAgencyFeature: PropTypes.string,
};

/**
 * Access denied component for role-based restrictions
 * @returns {JSX.Element}
 */
function AccessDenied() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="text-center space-y-6 max-w-md">
        <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
          <Settings className="h-10 w-10 text-destructive" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Acceso Denegado
          </h1>
          <p className="text-muted-foreground">
            No tienes permisos para acceder a esta sección. Si crees que esto es
            un error, contacta con tu administrador.
          </p>
        </div>
        <div className="pt-4">
          <button
            onClick={() => window.history.back()}
            className="text-primary hover:text-primary/80 font-medium"
          >
            ← Volver atrás
          </button>
        </div>
      </div>
    </div>
  );
}
